/**
 * 開設ウィザード（08 §5.2・FR-01/FR-73）＝運営が新規テナントを開設する3ステップ。
 *
 * 1. テナント名・プラン（任意）
 * 2. 初期管理者のメール・氏名
 * 3. 確認 → 開設
 *
 * 開設すると初期管理者の**パスワード設定URL（ワンタイム・24h）**が返る。これは
 * このレスポンスにしか無い（DB は SHA-256）ので、画面に1回だけ表示し、導入担当が
 * 本人へ伝達する（08 §5.2・LC-01/02＝自動送信ではなく人が渡す）。
 */

import { Button, Card, ErrorNote, Field, formatDateTime, PageHeader } from "@kenzai/ui";
import { ApiError, ops, type OpenTenantResult } from "@kenzai/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// backend の field_errors（server 側の値域＝EmailStr・name≤200 等）を、どの欄の問題かが分かる
// 日本語ラベルに写す＝FE の緩い検査を通った値でも、サーバに弾かれたら欄が特定できる（レビュー #1/#3）。
const FIELD_LABEL: Record<string, string> = {
  name: "テナント名",
  plan: "プラン",
  admin_email: "メールアドレス",
  admin_name: "氏名",
};

export function TenantOpen() {
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");

  const open = useMutation({
    mutationFn: () =>
      ops.openTenant({
        name: name.trim(),
        plan: plan.trim() || null,
        admin_email: adminEmail.trim(),
        admin_name: adminName.trim(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-tenants"] }),
  });

  if (open.isSuccess) return <Result result={open.data} />;

  const step1Valid = name.trim() !== "";
  const step2Valid = adminName.trim() !== "" && EMAIL_RE.test(adminEmail.trim());

  return (
    <>
      <PageHeader title="テナントを開設" subtitle={`ステップ ${step} / 3`} />
      <ErrorNote error={open.error} />
      {open.error instanceof ApiError && open.error.fieldErrors.length > 0 && (
        <ul
          role="alert"
          style={{
            margin: "0 0 var(--kz-space-3)",
            padding: "var(--kz-space-3) var(--kz-space-3) var(--kz-space-3) var(--kz-space-5)",
            background: "var(--kz-status-danger-weak)",
            color: "var(--kz-status-danger)",
            border: "1px solid currentColor",
            borderRadius: "var(--kz-radius-sm)",
            fontSize: "var(--kz-font-size-sm)",
          }}
        >
          {[...new Set(open.error.fieldErrors.map((fe) => FIELD_LABEL[fe.field] ?? fe.field))].map((label) => (
            <li key={label}>{label} をご確認ください（戻って修正できます）。</li>
          ))}
        </ul>
      )}
      <Card style={{ maxWidth: 560 }}>
        {step === 1 && (
          <>
            <StepTitle>1. テナント（卸）の情報</StepTitle>
            <Field
              label="テナント名"
              placeholder="サンプル建材株式会社"
              data-testid="wiz-name"
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Field
              label="プラン（任意）"
              hint="料金プランが決まっていなければ空欄で構いません。"
              placeholder="スタンダード"
              data-testid="wiz-plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
            />
            <Footer
              right={
                <div style={{ width: 160 }}>
                  <Button fullWidth data-testid="wiz-next" disabled={!step1Valid} onClick={() => setStep(2)}>
                    次へ
                  </Button>
                </div>
              }
            />
          </>
        )}

        {step === 2 && (
          <>
            <StepTitle>2. 初期管理者</StepTitle>
            <p style={{ margin: "0 0 var(--kz-space-4)", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
              この方が最初に卸コンソールへログインします。あとから追加・変更できます。
            </p>
            <Field
              label="氏名"
              placeholder="山田 太郎"
              data-testid="wiz-admin-name"
              maxLength={100}
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
            />
            <Field
              label="メールアドレス"
              type="email"
              placeholder="admin@example.co.jp"
              data-testid="wiz-admin-email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
            />
            <Footer
              left={
                <div style={{ width: 120 }}>
                  <Button fullWidth variant="ghost" data-testid="wiz-back" onClick={() => setStep(1)}>
                    戻る
                  </Button>
                </div>
              }
              right={
                <div style={{ width: 160 }}>
                  <Button fullWidth data-testid="wiz-next" disabled={!step2Valid} onClick={() => setStep(3)}>
                    次へ
                  </Button>
                </div>
              }
            />
          </>
        )}

        {step === 3 && (
          <>
            <StepTitle>3. 内容の確認</StepTitle>
            <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--kz-space-2) var(--kz-space-4)", fontSize: "var(--kz-font-size-md)" }}>
              <Dt>テナント名</Dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{name.trim()}</dd>
              <Dt>プラン</Dt>
              <dd style={{ margin: 0 }}>{plan.trim() || "—"}</dd>
              <Dt>初期管理者</Dt>
              <dd style={{ margin: 0 }}>{adminName.trim()}</dd>
              <Dt>メール</Dt>
              <dd style={{ margin: 0 }} className="kz-num">
                {adminEmail.trim()}
              </dd>
            </dl>
            <p style={{ margin: "var(--kz-space-4) 0 0", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
              開設すると、初期管理者のパスワード設定URLが表示されます。
            </p>
            <Footer
              left={
                <div style={{ width: 120 }}>
                  <Button fullWidth variant="ghost" data-testid="wiz-back" onClick={() => setStep(2)}>
                    戻る
                  </Button>
                </div>
              }
              right={
                <div style={{ width: 200 }}>
                  <Button fullWidth data-testid="wiz-submit" busy={open.isPending} onClick={() => open.mutate()}>
                    このテナントを開設
                  </Button>
                </div>
              }
            />
          </>
        )}
      </Card>
      <p style={{ marginTop: "var(--kz-space-4)" }}>
        <Link to="/" style={{ color: "var(--kz-color-primary)", fontSize: "var(--kz-font-size-sm)" }}>
          ← テナント一覧へ戻る
        </Link>
      </p>
    </>
  );
}

/** 開設の結果＝**このURLは一度しか出せない**（08 §5.2）。導入担当が本人へ渡す。 */
function Result({ result }: { result: OpenTenantResult }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.setup_path);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // クリップボードが使えない環境＝手で選択してコピーしてもらう（下に全文を表示済み）
      setCopied(false);
    }
  };

  return (
    <>
      <PageHeader
        title="テナントを開設しました"
        subtitle={`${result.tenant.code} ${result.tenant.name}`}
      />
      <Card style={{ maxWidth: 640, borderColor: "var(--kz-color-primary)" }}>
        <h2 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
          初期管理者のパスワード設定URL
        </h2>
        <p style={{ margin: "0 0 var(--kz-space-4)", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          卸コンソールのURLに続けて開いてください（このパスだけでは開けません）。
        </p>
        <div
          data-testid="wiz-setup-path"
          className="kz-num"
          style={{
            padding: "var(--kz-space-3)",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-surface-sunken)",
            border: "1px solid var(--kz-color-border)",
            fontSize: "var(--kz-font-size-sm)",
            wordBreak: "break-all",
          }}
        >
          {result.setup_path}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-3)", marginTop: "var(--kz-space-3)" }}>
          <div style={{ width: 140 }}>
            <Button fullWidth variant="ghost" data-testid="wiz-copy" onClick={copy}>
              コピー
            </Button>
          </div>
          {copied && (
            <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-primary)" }}>
              コピーしました
            </span>
          )}
        </div>
        <p
          role="note"
          style={{
            margin: "var(--kz-space-4) 0 0",
            padding: "var(--kz-space-3)",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-status-warn-weak)",
            color: "var(--kz-status-warn)",
            fontSize: "var(--kz-font-size-sm)",
          }}
        >
          このURLは一度だけ表示されます。
          <span className="kz-num"> {formatDateTime(result.expires_at)}</span> に失効します（24時間）。
          初期管理者ご本人にお伝えください。
        </p>
      </Card>
      <p style={{ marginTop: "var(--kz-space-4)" }}>
        <Link to="/" style={{ color: "var(--kz-color-primary)", fontSize: "var(--kz-font-size-sm)" }}>
          ← テナント一覧へ戻る
        </Link>
      </p>
    </>
  );
}

function StepTitle({ children }: { children: React.ReactNode }) {
  return <h2 style={{ margin: "0 0 var(--kz-space-4)", fontSize: "var(--kz-font-size-md)" }}>{children}</h2>;
}

function Dt({ children }: { children: React.ReactNode }) {
  return <dt style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>{children}</dt>;
}

function Footer({ left, right }: { left?: React.ReactNode; right: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginTop: "var(--kz-space-5)", gap: "var(--kz-space-3)" }}>
      {left}
      <div style={{ marginLeft: "auto" }}>{right}</div>
    </div>
  );
}
